import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalWrapper, Button, Checkbox, Prompt } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useGetProjectsQuery } from "@/features/projects/api/projectsApi";
import { useAssignVolunteerProjectsMutation } from "../api";

interface Props {
  volunteerId: number;
  onClose: () => void;
  onAssigned: () => void;
}

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

export default function VolunteerAssignProjectsModal({ volunteerId, onClose, onAssigned }: Props) {
  const { t } = useTranslation("VolunteersPage");
  const { data: projects = [], isLoading: isLoadingProjects } = useGetProjectsQuery();
  const [assignProjects] = useAssignVolunteerProjectsMutation();
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  function handleToggleProject(projectId: number) {
    setSelectedProjectIds(function toggleId(prev) {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId);
      }
      return [...prev, projectId];
    });
  }

  async function handleAssign() {
    if (isSubmitting || selectedProjectIds.length === 0) return;
    try {
      setIsSubmitting(true);
      await assignProjects({ id: volunteerId, project_ids: selectedProjectIds }).unwrap();
      onAssigned();
      onClose();
    } catch (err: unknown) {
      setPrompt({
        title: t("prompt.error.title"),
        text: extractErrorMessage(err, "prompt.error.assignFailed", t),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalWrapper onClose={onClose} width="w-md">
      <h2 className="text-display-2xs content-base-primary pr-10">{t("assignProjectsModal.title")}</h2>

      <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto component-scroll">
        {isLoadingProjects && (
          <p className="text-body-regular-md content-base-secondary py-4 text-center">
            {t("assignProjectsModal.loading")}
          </p>
        )}

        {!isLoadingProjects && projects.length === 0 && (
          <p className="text-body-regular-md content-base-secondary py-4 text-center">
            {t("assignProjectsModal.noProjects")}
          </p>
        )}

        {projects.map(function renderProjectItem(project) {
          const isSelected = selectedProjectIds.includes(project.id);
          return (
            <div
              key={project.id}
              role="checkbox"
              aria-checked={isSelected}
              tabIndex={0}
              className="flex items-center gap-3 p-3 radius-sm surface-base-fill cursor-pointer hover:surface-secondary-fill"
              onClick={() => handleToggleProject(project.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleToggleProject(project.id);
                }
              }}
            >
              <Checkbox
                state={isSelected ? "checked" : "unchecked"}
                onChange={() => handleToggleProject(project.id)}
              />
              <span className="text-body-regular-md content-base-primary">{project.name}</span>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="secondary" size="lg" onClick={onClose} disabled={isSubmitting}>
          {t("assignProjectsModal.buttons.cancel")}
        </Button>
        <Button
          variant="primary"
          size="lg"
          onClick={handleAssign}
          disabled={isSubmitting || selectedProjectIds.length === 0}
          textDuringDisabled={selectedProjectIds.length === 0 ? t("assignProjectsModal.rules.noProjects") : undefined}
          fullWidthTooltip
        >
          {isSubmitting
            ? t("assignProjectsModal.buttons.loading")
            : t("assignProjectsModal.buttons.assign", { count: selectedProjectIds.length })}
        </Button>
      </div>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="VolunteersPage"
        />
      )}
    </ModalWrapper>
  );
}
